import { shouldAutoFuse } from "./auto-fuse";
import { computeSystemCableSpecs } from "./cable-spec";
import { routeExample, viaX } from "./example-layout";
import { SCHEMA_VERSION } from "./schema/version";
import type { Cable, ComponentNode, System } from "./types";

function load(id: string, label: string, y: number, ampDraw: number): ComponentNode {
  return {
    id, kind: "component", definitionId: "custom", label, position: { x: 1040, y },
    customDefinition: {
      id: `single-boat-${id}`, name: label, category: "load", voltage: 12, ampDraw,
      width: 220, height: 110,
      terminals: [
        { id: "pos", label: "+", polarity: "positive", direction: "input", side: "left", offset: 0.3, maxAmp: ampDraw },
        { id: "neg", label: "−", polarity: "negative", direction: "input", side: "left", offset: 0.7, maxAmp: ampDraw },
      ],
    },
    terminalOverrides: { pos: { side: "left", offset: 0.3, pinned: true }, neg: { side: "left", offset: 0.7, pinned: true } },
    notes: `${ampDraw} A continuous planning allowance, not a specific product or measured current. Replace with the actual device, startup demand and manufacturer fuse instructions. Terminal limits are placeholders.`,
  };
}

function conductor(id: string, from: [string, string], to: [string, string], lengthM: number): Cable {
  return { id, fromNodeId: from[0], fromTerminalId: from[1], toNodeId: to[0], toTerminalId: to[1], lengthM, vdropClass: "critical" };
}

/** One switched DC load group. No engine, charging source or always-on bilge
 * circuit is implied. Exact protective devices and their physical placement
 * are deliberately unverified, so the editor keeps its real diagnostics. */
export function buildSingleBatteryBoatSystem(): System {
  const system: System = {
    name: "Single-battery boat — lights and electronics", version: SCHEMA_VERSION, zoneDistances: {},
    nodes: [
      {
        id: "battery", kind: "component", definitionId: "batt-agm-100", label: "12 V AGM example battery", position: { x: 0, y: 170 },
        terminalOverrides: { pos: { side: "right", offset: 0.3, pinned: true }, neg: { side: "bottom", offset: 0.5, pinned: true } },
        notes: "Generic 100 Ah AGM catalog placeholder, not a selected battery or runtime recommendation. Confirm battery specifications, available fault current, charging and battery-end protection for the actual installation. The diagram covers accessory loads only.",
      },
      {
        id: "isolation", kind: "component", definitionId: "dist-bs-mseries-6005", label: "Battery on/off switch", position: { x: 340, y: 180 },
        notes: "Blue Sea 6005 single-circuit ON/OFF switch. This disconnects both illustrated accessory branches together. It is not an overcurrent device. Engine starting, charging and circuits that must remain powered are outside this drawing. Source: Blue Sea 6005 product page, checked 21 October 2026.",
      },
      {
        id: "distribution", kind: "component", definitionId: "dist-bs-5025", label: "Blue Sea 5025 fuse block", position: { x: 540, y: 150 },
        terminalOverrides: {
          "in-pos": { side: "left", offset: 0.25, pinned: true }, "in-neg": { side: "left", offset: 0.75, pinned: true },
          c1: { side: "right", offset: 0.1, pinned: true }, n1: { side: "right", offset: 0.3, pinned: true },
          c2: { side: "right", offset: 0.7, pinned: true }, n2: { side: "right", offset: 0.9, pinned: true },
        },
        notes: "Blue Sea 5025 with negative bus: 6 ATO/ATC branches, 100 A maximum block total, 30 A maximum per branch, 32 V DC. Two branches are used here. Branch fuses are separate from the battery-feed protection; fuses are sold separately. Source: Blue Sea product page and linked schematic, checked 21 October 2026.",
      },
      load("lights", "Deck / cabin lights", 30, 2),
      load("electronics", "Fish finder / electronics", 310, 5),
    ],
    cables: [
      conductor("battery-feed", ["battery", "pos"], ["isolation", "a"], 0.25),
      conductor("panel-feed", ["isolation", "b"], ["distribution", "in-pos"], 0.75),
      conductor("battery-return", ["distribution", "in-neg"], ["battery", "neg"], 1),
      conductor("lights-feed", ["distribution", "c1"], ["lights", "pos"], 3),
      conductor("lights-return", ["lights", "neg"], ["distribution", "n1"], 3),
      conductor("electronics-feed", ["distribution", "c2"], ["electronics", "pos"], 2),
      conductor("electronics-return", ["electronics", "neg"], ["distribution", "n2"], 2),
    ],
  };
  const specs = computeSystemCableSpecs(system);
  const fused = new Set(["battery-feed", "lights-feed", "electronics-feed"]);
  return routeExample({ ...system, cables: system.cables.map((cable, index) => {
    const spec = specs.get(cable.id)!;
    return { ...cable, label: `C${index + 1}`, ...(fused.has(cable.id) && shouldAutoFuse(spec) ? { fuseAmpOverride: spec.fuse, fuseAmpAuto: true } : {}) };
  }) }, {
    "battery-feed": viaX(270),
    "panel-feed": viaX(460),
    "battery-return": (from, to) => [{ x: 490, y: from.y }, { x: 490, y: 410 }, { x: to.x, y: 410 }],
    "lights-feed": viaX(920), "lights-return": viaX(960),
    "electronics-feed": viaX(960), "electronics-return": viaX(920),
  });
}
